import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import api from '../lib/api';
import { getAuth } from '../lib/auth';
import { MainLayout } from '../components/MainLayout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Upload,
  Eye,
  Download,
  Trash2,
  Loader2,
  FileSpreadsheet,
  ListTodo,
  CheckCircle2,
  AlertTriangle,
  Settings2,
  Clock,
  DollarSign
} from 'lucide-react';
import { format } from 'date-fns';

// ═══════════════════════════════════════════════════════════════════
// ROW STATUS BADGES - Compact badges for table
// ═══════════════════════════════════════════════════════════════════

const RowStatusBadges = ({ validated = 0, corrected = 0, total = 0 }) => {
  return (
    <div className="flex items-center gap-1 text-sm">
      <span className="inline-flex items-center justify-center min-w-[32px] px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 font-medium">
        {validated}
      </span>
      <span className="text-muted-foreground">/</span>
      <span className="inline-flex items-center justify-center min-w-[32px] px-2 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">
        {corrected}
      </span>
      <span className="text-muted-foreground">/</span>
      <span className="text-muted-foreground">{total}</span>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════

const PaymentVerificationV2 = () => {
  const navigate = useNavigate();
  const { user } = getAuth();
  
  const [currentAccount, setCurrentAccount] = useState(() => {
    const str = localStorage.getItem('current_account');
    return str ? JSON.parse(str) : null;
  });
  
  useEffect(() => {
    const checkAccount = () => {
      const str = localStorage.getItem('current_account');
      const acc = str ? JSON.parse(str) : null;
      if (acc?.id && acc.id !== currentAccount?.id) {
        setCurrentAccount(acc);
      }
    };
    checkAccount();
    const interval = setInterval(checkAccount, 500);
    const timeout = setTimeout(() => clearInterval(interval), 10000);
    return () => { clearInterval(interval); clearTimeout(timeout); };
  }, [currentAccount?.id]);

  // State
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [batches, setBatches] = useState([]);
  const [stats, setStats] = useState({
    payment_files_processed: 0,
    ready_for_review: 0,
    approved: 0
  });
  const [activeTab, setActiveTab] = useState('files');
  const [duplicateDialog, setDuplicateDialog] = useState({ open: false, data: null });
  const [accordionValue, setAccordionValue] = useState(''); // Collapsed by default

  // Fetch data using V2 API
  const fetchData = useCallback(async () => {
    if (!currentAccount?.id) return;
    
    setLoading(true);
    try {
      const [batchesRes, statsRes] = await Promise.all([
        api.get(`/payment-verification/v2/batches?account_id=${currentAccount.id}`),
        api.get(`/payment-verification/v2/stats?account_id=${currentAccount.id}`)
      ]);
      
      const batchesData = batchesRes.data.batches || [];
      setBatches(batchesData);
      setStats(statsRes.data.stats || {});
      
      // Auto-switch to queue if there are processing batches
      const hasProcessingBatches = batchesData.some(b => 
        b.status === 'uploaded' || b.status === 'processing'
      );
      
      if (hasProcessingBatches) {
        setActiveTab('queue');
      }
    } catch (error) {
      console.error('Error fetching payment data:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to load payment data: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  }, [currentAccount?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // File upload handler using V2 API
  const onDrop = useCallback(async (acceptedFiles) => {
    if (!currentAccount?.id || !user?.id) return;
    
    const file = acceptedFiles[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Only CSV files are supported');
      return;
    }
    
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post(
        `/payment-verification/v2/batches/upload?account_id=${currentAccount.id}&user_id=${user.id}&user_name=${encodeURIComponent(user.name)}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      if (response.data.status === 'duplicate') {
        setDuplicateDialog({
          open: true,
          data: response.data.existing_batch
        });
      } else {
        toast.success('File uploaded! Processing in background...');
        setActiveTab('queue');
        setAccordionValue(''); // Collapse accordion after upload
        fetchData();
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  }, [currentAccount?.id, user, fetchData]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'text/csv': ['.csv'] },
    multiple: false,
    disabled: uploading
  });

  // Separate batches by status
  const jobQueueBatches = useMemo(() => 
    batches.filter(b => b.status === 'uploaded' || b.status === 'processing' || b.status === 'failed'),
    [batches]
  );
  
  const readyBatches = useMemo(() => {
    const filtered = batches.filter(b => 
      (b.status === 'ready' || b.status === 'ops_approved' || b.status === 'customer_approved') && 
      !b.engine_paid && b.engine_status !== 'Paid'
    );
    // Sort by engine_generated_at (latest first), fall back to uploaded_at
    return filtered.sort((a, b) => {
      const dateA = a.engine_generated_at || a.uploaded_at || '';
      const dateB = b.engine_generated_at || b.uploaded_at || '';
      return dateB.localeCompare(dateA);
    });
  }, [batches]);
  
  const paidBatches = useMemo(() => {
    const filtered = batches.filter(b => 
      b.engine_paid || b.engine_status === 'Paid' || (b.total_paid > 0 && b.total_paid >= (b.total_funds_requested || b.total_original_requested || 1))
    );
    return filtered.sort((a, b) => {
      const dateA = a.engine_generated_at || a.uploaded_at || '';
      const dateB = b.engine_generated_at || b.uploaded_at || '';
      return dateB.localeCompare(dateA);
    });
  }, [batches]);

  // Auto-refresh when there are batches processing
  useEffect(() => {
    const hasProcessingBatches = batches.some(b => b.status === 'uploaded' || b.status === 'processing');
    if (hasProcessingBatches) {
      const interval = setInterval(fetchData, 3000);
      return () => clearInterval(interval);
    }
  }, [batches, fetchData]);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // Handle download
  const handleDownload = async (batchId, filename) => {
    try {
      const response = await api.get(
        `/payment-verification/v2/batches/${batchId}/download?account_id=${currentAccount.id}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `corrected_${filename}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Download started');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
    }
  };

  // Handle delete
  const handleDeleteBatch = async (batchId) => {
    if (!confirm('Are you sure you want to delete this batch?')) return;
    
    try {
      await api.delete(
        `/payment-verification/v2/batches/${batchId}?account_id=${currentAccount.id}`
      );
      toast.success('Batch deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete batch');
    }
  };

  // Format batch ID for display
  const formatBatchId = (batchId) => {
    if (!batchId) return '-';
    // Format: PV-YYYYMMDDHHMMSS-shortid
    const parts = batchId.split('-');
    const shortId = parts[parts.length - 1]?.slice(0, 8) || batchId.slice(-8);
    const timestamp = new Date().toISOString().replace(/[-:T]/g, '').slice(0, 14);
    return `PV-${timestamp}-${shortId}`;
  };

  return (
    <MainLayout>
      <div className="container mx-auto px-6 md:px-12 py-8" data-testid="payment-verification-page">
        {/* Header Row - Title Left, Config Button Right */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">Payment Verification</h1>
            <p className="text-muted-foreground">Validate and correct payment file requests</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/payment-verification/config')}
            data-testid="config-button"
          >
            <Settings2 className="h-4 w-4 mr-2" />
            Payment Config
          </Button>
        </div>

        {/* Upload Accordion */}
        <Accordion 
          type="single" 
          collapsible 
          value={accordionValue}
          onValueChange={setAccordionValue}
          className="mb-6"
        >
          <AccordionItem value="upload" className="border rounded-lg">
            <AccordionTrigger className="px-4 py-3 hover:no-underline" data-testid="upload-accordion-trigger">
              <div className="flex items-center gap-2">
                <Upload className="h-4 w-4" />
                <span>Upload New Bill</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
                  ${isDragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'}
                  ${uploading ? 'pointer-events-none opacity-50' : ''}`}
                data-testid="upload-dropzone"
              >
                <input {...getInputProps()} />
                {uploading ? (
                  <div className="flex flex-col items-center">
                    <Loader2 className="h-10 w-10 animate-spin text-primary mb-2" />
                    <p className="text-muted-foreground">Processing file...</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <Upload className="h-10 w-10 text-muted-foreground mb-2" />
                    <p className="text-muted-foreground">
                      {isDragActive ? 'Drop the CSV file here' : 'Drag & drop a CSV file here, or click to select'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">Only .csv files are supported</p>
                  </div>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-card border rounded-lg p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <FileSpreadsheet className="h-4 w-4" />
              <span className="text-sm">Files Processed</span>
            </div>
            <p className="text-2xl font-bold">{stats.payment_files_processed}</p>
          </div>
          
          <div className="bg-card border rounded-lg p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <AlertTriangle className="h-4 w-4" />
              <span className="text-sm">Ready for Review</span>
            </div>
            <p className="text-2xl font-bold text-amber-500">{stats.ready_for_review}</p>
          </div>
          
          <div className="bg-card border rounded-lg p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <CheckCircle2 className="h-4 w-4" />
              <span className="text-sm">Approved</span>
            </div>
            <p className="text-2xl font-bold text-emerald-500">{stats.approved}</p>
          </div>
        </div>

        {/* Tabs with Table */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="queue" className="flex items-center gap-2" data-testid="tab-queue">
              <ListTodo className="h-4 w-4" />
              Job Queue
              {jobQueueBatches.length > 0 && (
                <Badge variant="secondary" className="ml-1 bg-blue-100 text-blue-700">{jobQueueBatches.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="files" className="flex items-center gap-2" data-testid="tab-files">
              <FileSpreadsheet className="h-4 w-4" />
              Payment Files
              {readyBatches.length > 0 && (
                <Badge variant="secondary" className="ml-1">{readyBatches.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="paid" className="flex items-center gap-2" data-testid="tab-paid">
              <DollarSign className="h-4 w-4" />
              Paid
              {paidBatches.length > 0 && (
                <Badge variant="secondary" className="ml-1 bg-green-100 text-green-700">{paidBatches.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Job Queue Tab */}
          <TabsContent value="queue">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : jobQueueBatches.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground border rounded-lg">
                <ListTodo className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No files in the queue</p>
                <p className="text-sm mt-2">Upload a file to start processing</p>
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="font-semibold text-xs uppercase tracking-wider">Batch ID</TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider">File</TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider">Status</TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider">Rows</TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider">Uploaded</TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {jobQueueBatches.map((batch) => (
                      <TableRow key={batch.batch_id} className="hover:bg-muted/30">
                        <TableCell className="font-mono text-xs text-muted-foreground">
                          {formatBatchId(batch.batch_id)}
                        </TableCell>
                        <TableCell>
                          <div>
                            <span className="font-medium">{batch.filename}</span>
                            {batch.error_message && (
                              <p className="text-xs text-red-500 mt-1 max-w-xs truncate" title={batch.error_message}>
                                {batch.error_message}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant={
                              batch.status === 'processing' ? 'default' : 
                              batch.status === 'failed' ? 'destructive' : 'secondary'
                            }
                            className="flex items-center gap-1 w-fit"
                          >
                            {batch.status === 'processing' && <Loader2 className="h-3 w-3 animate-spin" />}
                            {batch.status === 'uploaded' && <Clock className="h-3 w-3" />}
                            {batch.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{batch.total_rows || '-'}</TableCell>
                        <TableCell>
                          <div>
                            <span className="text-sm">
                              {batch.uploaded_at ? format(new Date(batch.uploaded_at), 'MMM d, yyyy h:mm a') : '-'}
                            </span>
                            {batch.uploaded_by_name && (
                              <p className="text-xs text-muted-foreground">by {batch.uploaded_by_name}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteBatch(batch.batch_id)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-queue-${batch.batch_id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>

          {/* Payment Files Tab */}
          <TabsContent value="files">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : readyBatches.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground border rounded-lg">
                <FileSpreadsheet className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No payment files</p>
                <p className="text-sm mt-2">Upload a CSV file to get started</p>
              </div>
            ) : (
              <div className="border rounded-lg overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="font-semibold text-xs uppercase tracking-wider whitespace-nowrap">File</TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider whitespace-nowrap">Rows</TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider whitespace-nowrap">Requested</TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider whitespace-nowrap">Received</TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider whitespace-nowrap">Paid</TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider whitespace-nowrap">Date</TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider text-right whitespace-nowrap w-[80px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {readyBatches.map((batch) => (
                      <TableRow key={batch.batch_id} className="hover:bg-muted/30">
                        <TableCell>
                          <div className="max-w-[220px]">
                            <span className="font-medium text-sm truncate block">{batch.filename}</span>
                            {batch.source === 'engine_pull' && (
                              <div className="flex items-center gap-1 mt-0.5">
                                <Badge variant="outline" className="text-xs px-1 py-0">Engine</Badge>
                                {batch.external_file_id && (
                                  <span className="text-xs text-muted-foreground font-mono">
                                    {batch.external_file_id.slice(0, 8)}...
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <RowStatusBadges
                            validated={batch.validated_rows || 0}
                            corrected={batch.corrected_rows || 0}
                            total={batch.total_rows || 0}
                          />
                        </TableCell>
                        <TableCell className="font-medium text-blue-700">
                          {formatCurrency(batch.total_funds_requested || batch.total_original_requested)}
                        </TableCell>
                        <TableCell className="font-medium text-purple-700">
                          {batch.total_funds_received > 0 
                            ? formatCurrency(batch.total_funds_received) 
                            : <span className="text-muted-foreground">-</span>
                          }
                        </TableCell>
                        <TableCell className="font-medium text-emerald-700">
                          {(batch.total_paid > 0 || batch.paid_invoice_count > 0) ? (
                            <div>
                              <span>{formatCurrency(batch.total_paid || 0)}</span>
                              {batch.paid_invoice_count > 0 && (
                                <p className="text-xs text-muted-foreground">{batch.paid_invoice_count} invoices</p>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="whitespace-nowrap">
                            {batch.engine_generated_at ? (
                              <>
                                <span className="text-xs">
                                  {format(new Date(batch.engine_generated_at), 'MMM d, yyyy')}
                                </span>
                                <p className="text-xs text-muted-foreground">Engine</p>
                              </>
                            ) : (
                              <span className="text-xs">
                                {batch.uploaded_at ? format(new Date(batch.uploaded_at), 'MMM d, yyyy') : '-'}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => navigate(`/payment-verification/${batch.batch_id}`)}
                              data-testid={`view-batch-${batch.batch_id}`}
                              title="View details"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDownload(batch.batch_id, batch.filename)}
                              data-testid={`download-batch-${batch.batch_id}`}
                              title="Download corrected file"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            {!batch.locked && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDeleteBatch(batch.batch_id)}
                                data-testid={`delete-batch-${batch.batch_id}`}
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>

          {/* Paid Files Tab */}
          <TabsContent value="paid">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : paidBatches.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground border rounded-lg">
                <DollarSign className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No paid payment files</p>
                <p className="text-sm mt-2">Files with paid status will appear here</p>
              </div>
            ) : (
              <div className="border rounded-lg overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="font-semibold text-xs uppercase tracking-wider whitespace-nowrap">File</TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider whitespace-nowrap">Rows</TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider whitespace-nowrap">Requested</TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider whitespace-nowrap">Paid Amount</TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider whitespace-nowrap">Date</TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider text-right whitespace-nowrap w-[80px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paidBatches.map((batch) => (
                      <TableRow key={batch.batch_id} className="hover:bg-muted/30">
                        <TableCell>
                          <div className="max-w-[220px]">
                            <span className="font-medium text-sm truncate block">{batch.filename}</span>
                            {batch.source === 'engine_pull' && (
                              <div className="flex items-center gap-1 mt-0.5">
                                <Badge variant="outline" className="text-xs px-1 py-0 bg-green-50 text-green-700 border-green-200">Paid</Badge>
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">{batch.total_rows || 0}</span>
                        </TableCell>
                        <TableCell className="font-medium text-blue-700 text-sm">
                          {formatCurrency(batch.total_funds_requested || batch.total_original_requested)}
                        </TableCell>
                        <TableCell className="font-medium text-emerald-700 text-sm">
                          {formatCurrency(batch.total_paid || batch.total_funds_requested || 0)}
                          {batch.paid_invoice_count > 0 && (
                            <p className="text-xs text-muted-foreground">{batch.paid_invoice_count} invoices</p>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="whitespace-nowrap">
                            {batch.engine_generated_at ? (
                              <>
                                <span className="text-xs">{format(new Date(batch.engine_generated_at), 'MMM d, yyyy')}</span>
                                <p className="text-xs text-muted-foreground">Engine</p>
                              </>
                            ) : (
                              <span className="text-xs">{batch.uploaded_at ? format(new Date(batch.uploaded_at), 'MMM d, yyyy') : '-'}</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => navigate(`/payment-verification/${batch.batch_id}`)}
                              data-testid={`view-paid-${batch.batch_id}`}
                              title="View details"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDownload(batch.batch_id, batch.filename)}
                              title="Download"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Duplicate File Dialog */}
        <Dialog open={duplicateDialog.open} onOpenChange={(open) => setDuplicateDialog({ ...duplicateDialog, open })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Duplicate File Detected</DialogTitle>
              <DialogDescription>
                This file has already been uploaded. Would you like to view the existing batch?
              </DialogDescription>
            </DialogHeader>
            {duplicateDialog.data && (
              <div className="py-4">
                <p className="text-sm text-muted-foreground">
                  Uploaded on {duplicateDialog.data.uploaded_at && 
                    format(new Date(duplicateDialog.data.uploaded_at), 'MMM d, yyyy h:mm a')}
                </p>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDuplicateDialog({ open: false, data: null })}>
                Cancel
              </Button>
              <Button onClick={() => {
                if (duplicateDialog.data?.batch_id) {
                  navigate(`/payment-verification/${duplicateDialog.data.batch_id}`);
                }
                setDuplicateDialog({ open: false, data: null });
              }}>
                View Existing
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};

export default PaymentVerificationV2;
